import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { HtmlContent } from '../components/HtmlContent';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { accommodationStayPresentation } from '../api';
import { IOnlineTourPresentationResult } from '../utils/types';
import { AuthContext } from '../context/AuthContext';
import { TransitionProps } from '@mui/material/transitions';
import Slide from '@mui/material/Slide';
import CloseIcon from '@mui/icons-material/Close';

type HotelPresentationInfoProps = {
  stayId: number;
  onEmptyDetails?: () => void;
  open: boolean;
  onClose: () => void;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

export function HotelPresentationInfoDialog({
  stayId,
  onEmptyDetails,
  open,
  onClose,
}: HotelPresentationInfoProps) {
  const { t } = useTranslation();
  const { token } = useContext(AuthContext);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [
    onlineTourDetailPresentationResult,
    setOnlineTourDetailPresentationResult,
  ] = useState<IOnlineTourPresentationResult | null>(null);

  useEffect(() => {
    if (open) {
      fetchAccommodationStayPresentations(stayId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchAccommodationStayPresentations = async (stayId: number) => {
    try {
      const res = await accommodationStayPresentation(stayId, token);
      const data = await res.json();
      if (res?.status === 200) {
        setOnlineTourDetailPresentationResult(data);
        if (!data.name && !data.description && onEmptyDetails) {
          onEmptyDetails();
        }
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (onEmptyDetails) onEmptyDetails();
      setError(e.detail || t('loginPage.registerErrorMessage'));
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      sx={{ opacity: 1 }}
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <DialogTitle sx={{ display: 'flex' }}>
        {t('accommodationCityCardPart.hotelInfo')}
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Fragment>
          <HtmlContent
            content={
              onlineTourDetailPresentationResult?.name ||
              t('onlineTourDetail.noNameYet')
            }
          />

          {onlineTourDetailPresentationResult?.description && (
            <Fragment>
              <Divider sx={{ my: 1 }} />
              <HtmlContent
                content={
                  onlineTourDetailPresentationResult?.description ||
                  'no description yet'
                }
              />
            </Fragment>
          )}
        </Fragment>
      </DialogContent>
    </Dialog>
  );
}
